import { HttpClient } from '@angular/common/http';
import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TOAST_DURATION } from '@banyan/commons/consts/global.const';
import { fromCartResponseToCartV2 } from '@banyan/commons/dtos/cart.dto';
import { CartV2 } from '@banyan/commons/models/cart/cart.model';
import { AddNewCartItemV2, EditCartItemV2 } from '@banyan/commons/models/cart/cart-item-payload.model';
import { CartResponseV2 } from '@banyan/commons/models/cart/cart-response.model';
import { ProgressSpinnerService } from '@banyan/modules/shared/services/progress-spinner/progress-spinner.service';
import { AlertComponent } from '@banyan/modules/standalone/alert/alert.component';
import { environment } from '@env/environment';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class CartV2Service {
  private readonly PATH: string = '/v2/cart';
  destroyRef = inject(DestroyRef);
  httpClient = inject(HttpClient);
  progressSpinnerService = inject(ProgressSpinnerService);
  translateService = inject(TranslateService);
  snackBar = inject(MatSnackBar);
  cart = signal<CartV2 | null>(null);
  cartLoaded = signal<boolean>(false);

  addCartLine(
    product: AddNewCartItemV2,
    requestId: number,
    briefId: number,
    fragranceProfileId: number
  ): void {
    this.progressSpinnerService.show();
    const param = { product, requestId, briefId, fragranceProfileId };
    this.httpClient.post<CartResponseV2>(
      `${environment.middlewareUrl}${this.PATH}`,
      param
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      {
        next: (result: CartResponseV2) => {
          this.callAlert(product.isSample);
          if (result) {
            this.cart.set(fromCartResponseToCartV2(result));
            this.cartLoaded.set(true);
          }
          this.progressSpinnerService.hide();
        },
        error: () => {
          this.cartLoaded.set(false);
        },
      });
  }

  updateCartLine(editCartLineItem: EditCartItemV2): void {
    const param = {
      quantity: editCartLineItem.quantity,
      bigCommerceProductId: editCartLineItem.bigCommerceProductId,
    };
    this.httpClient.put<CartResponseV2>(
      `${environment.middlewareUrl}${this.PATH}/${editCartLineItem.lineId}`,
      param
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      {
        next: (result: CartResponseV2) => {
          if (result) {
            this.cart.set(fromCartResponseToCartV2(result));
            this.cartLoaded.set(true);
          }
        },
        error: () => {
          this.cartLoaded.set(false);
        },
      });
  }

  deleteCartLine(lineId: string): void {
    this.progressSpinnerService.show();
    this.httpClient.delete<CartResponseV2>(
      `${environment.middlewareUrl}${this.PATH}/${lineId}`,
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      {
        next: (result: CartResponseV2) => {
          if (result) {
            this.cart.set(fromCartResponseToCartV2(result));
          } else {
            this.cart.set(null);
          }
          this.cartLoaded.set(true);
          this.progressSpinnerService.hide();
        },
        error: () => {
          this.cartLoaded.set(false);
        },
      });
  }

  updateCart(bigCommerceProductId: number | null,
             briefId: number | null,
             fragranceProfileId: number | null,
             isSample: boolean | null,
             lineId: string | null,
             quantity: number | null,
             requestId: number | null = null): void {
    if (lineId) {
      if (bigCommerceProductId && quantity && quantity > 0) {
        const editCartLineItem: EditCartItemV2 = {
          lineId,
          quantity,
          bigCommerceProductId: bigCommerceProductId,
        };
        this.updateCartLine(editCartLineItem);
        return;
      }
      this.deleteCartLine(lineId);
      return;
    }
    const product: AddNewCartItemV2 = {
      isSample: isSample ?? false,
      bigCommerceProductId: bigCommerceProductId,
      quantity,
    };

    if (requestId && briefId && fragranceProfileId) {
      this.addCartLine(product, requestId, briefId, fragranceProfileId);
    }
  }

  getCart(): void {
    this.cartLoaded.set(false);
    this.httpClient.get<CartResponseV2>(
      `${environment.middlewareUrl}${this.PATH}`,
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      {
        next: (result: CartResponseV2) => {
          if (result) {
            this.cart.set(fromCartResponseToCartV2(result));
            this.cartLoaded.set(true);
          }
        },
        error: () => {
          this.cartLoaded.set(false);
        },
      });
  }

  callAlert(isSample: boolean): void {
    const params = {
      text:
        isSample
          ? this.translateService.instant('cart.sample-added-to-basket')
          : this.translateService.instant('cart.fragrance-added-to-basket'),
      icon: 'shopping_basket',
      button: this.translateService.instant('cart.open-basket'),
      url: '/protected/cart/V2',
    };
    this.snackBar.openFromComponent(AlertComponent, {
      data: params,
      duration: TOAST_DURATION,
    });
  }
}
